import type { Item } from './api.ts';

export type RepeatMode = 'off' | 'all' | 'one';

export interface AudioQueue {
  items: Item[];
  index: number;
}

/** Aynı parçayı bir kez tutar; geçerli parça listede yoksa sona ekler. */
export function normalizeQueue(items: Item[], current: Item): AudioQueue {
  const seen = new Set<string>();
  const unique = items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
  const index = unique.findIndex((item) => item.id === current.id);
  if (index >= 0) return { items: unique, index };
  return { items: [...unique, current], index: unique.length };
}

export function nextQueueIndex(
  queue: AudioQueue,
  repeat: RepeatMode,
  shuffle: boolean,
  random: () => number = Math.random,
): number | null {
  if (queue.items.length === 0) return null;
  if (shuffle && queue.items.length > 1) {
    const candidate = Math.floor(random() * (queue.items.length - 1));
    return candidate >= queue.index ? candidate + 1 : candidate;
  }
  const next = queue.index + 1;
  if (next < queue.items.length) return next;
  return repeat === 'all' ? 0 : null;
}

export function previousQueueIndex(queue: AudioQueue, repeat: RepeatMode): number | null {
  if (queue.items.length === 0) return null;
  const previous = queue.index - 1;
  if (previous >= 0) return previous;
  return repeat === 'all' ? queue.items.length - 1 : null;
}

export function removeQueueItem(queue: AudioQueue, itemId: string): AudioQueue {
  const index = queue.items.findIndex((item) => item.id === itemId);
  if (index < 0 || index === queue.index) return queue;
  const items = queue.items.filter((item) => item.id !== itemId);
  return { items, index: index < queue.index ? queue.index - 1 : queue.index };
}

export function moveQueueItem(queue: AudioQueue, from: number, to: number): AudioQueue {
  if (from === to || from < 0 || to < 0 || from >= queue.items.length || to >= queue.items.length) {
    return queue;
  }
  const items = [...queue.items];
  const [moved] = items.splice(from, 1);
  if (moved === undefined) return queue;
  items.splice(to, 0, moved);

  let index = queue.index;
  if (queue.index === from) index = to;
  else if (from < queue.index && to >= queue.index) index = queue.index - 1;
  else if (from > queue.index && to <= queue.index) index = queue.index + 1;
  return { items, index };
}

/**
 * Bir parçayı sıraya ekler (aynı parça iki kez eklenmez).
 */
export function appendToQueue(queue: AudioQueue, item: Item): AudioQueue {
  if (queue.items.some((entry) => entry.id === item.id)) return queue;
  return { items: [...queue.items, item], index: queue.index };
}

/**
 * Albümden sıra kurar: **tıklanan parçadan sonrası**.
 *
 * ## Neden?
 *
 * Tek bir parça açıldığında sıraya **tüm kütüphane** ekleniyordu (168 parça).
 * Bu hem paneli dolduruyor hem de "sonraki" komutunun kütüphane sırasında
 * ilerlemesine yol açıyordu — albüm dinlenmiyordu.
 *
 * Beklenen davranış (Plex/Jellyfin/Spotify): parçanın **albümü** sıraya girer
 * ve tıklanan parçadan itibaren akar.
 */
export function queueFromAlbum(tracks: Item[], currentId: string): Item[] {
  const index = tracks.findIndex((track) => track.id === currentId);
  if (index < 0) return [...tracks];
  return tracks.slice(index);
}
